import React from 'react';
import styled, { keyframes } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.background.secondary} 25%,
    ${({ theme }) => theme.special.skeletonLight} 50%,
    ${({ theme }) => theme.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
`;

const BalanceContainer = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  padding: 16px;
  margin-top: 20px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  margin-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
`;

const Thead = styled.thead`
  background-color: ${({ theme }) => theme.background.light};
  text-align: left;
  font-size: 14px;
  color: ${({ theme }) => theme.text.secondary};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Th = styled.th`
  padding: 16px;
`;

const Td = styled.td`
  padding: 16px;
  vertical-align: middle;
`;

const AssetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SkeletonIcon = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`;

const SkeletonImage = styled(SkeletonBase)`
  width: 136px;
  height: 72px;
  border-radius: 8px;
`;

const SkeletonText = styled(SkeletonBase)`
  height: 16px;
  margin: 4px 0;
`;

const SkeletonLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SkeletonBalance = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = ({ isMobile }) => (
  <Tr>
    {!isMobile && (
      <Td>
        <SkeletonBase style={{ width: '16px', height: '16px' }} />
      </Td>
    )}
    <Td>
      <AssetInfo>
        {!isMobile && <SkeletonImage />}
        <SkeletonIcon />
        <SkeletonLabel>
          <SkeletonText style={{ width: '100px' }} />
          {!isMobile && <SkeletonText style={{ width: '60px' }} />}
        </SkeletonLabel>
      </AssetInfo>
    </Td>
    <Td>
      <SkeletonBalance>
        <SkeletonText style={{ width: '80px' }} />
        <SkeletonText style={{ width: '60px' }} />
      </SkeletonBalance>
    </Td>
  </Tr>
);

const PortfolioSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  return (
    <>
      <BalanceContainer>
        <SkeletonText style={{ width: '100px', marginBottom: '8px' }} />
        <SkeletonText style={{ width: '150px', height: '24px' }} />
      </BalanceContainer>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              {!isMobile && <Th style={{ width: '88px' }}>#</Th>}
              <Th>Asset</Th>
              <Th>Amount</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[...Array(5)].map((_, i) => (
              <Row key={i} isMobile={isMobile} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PortfolioSkeleton;
